import React, { useEffect, useMemo, useState, useRef } from "react"
import { flushSync } from 'react-dom'
import { useNavigate, Link, useParams } from 'react-router'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Modal, Dropdown, DropdownButton } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from "react-datepicker"

import ReactSelect from 'pages/parts/ReactSelect'
import useVirtualRows from 'pages/parts/useVirtualRows'
import usePreventExit from "pages/parts/usePreventExit"
import DocumentTemplate from 'pages/parts/DocumentTemplate'
import NotInventored from "pages/main/inventory/NotInventored"
import useDidMountEffect from 'pages/parts/useDidMountEffect'
import { httpOk, GET, POST, PUT, DELETE, FILE, FILE_D } from 'helpers/api'
import { formatDate, formatMoney, formatInventoryQty, returnMinDate, notify } from 'helpers/helpers'
import { handleDecimalKeyDown } from 'helpers/decimalKey'

// Высота строки таблицы — должна совпадать с .doc-table tbody td в style.css
const ROW_HEIGHT = 33
// Сколько позиций справочника показываем в выпадающем списке за раз
const OPTIONS_LIMIT = 50
// «Элемент» в справочнике типов идёт нулевым: обычно инвентаризуют именно его,
// остальные типы клиент отмечает галочками сам
const PRODUCT_TYPE_ELEMENT = 0

function CreateUpdate() {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();

	const [isDirty, setIsDirty] = useState(false);
	usePreventExit(isDirty);

	const tbodyRef = useRef()
	const scrollRef = useRef()

	const reduxAccount = useSelector(state => state.account)

	const [wms, setWms] = useState([])
	const [elements, setElements] = useState([])
	const [elementsDuplicate, setElementsDuplicate] = useState([])
	const [modalConfirm, setModalConfirm] = useState(false)
	const [modalNotInventored, setModalNotInventored] = useState(false)
	const [addQuantity, setAddQuantity] = useState(1)
	const [isBarcodeSearchEnabled, setIsBarcodeSearchEnabled] = useState(true)
	const [notFoundImportItems, setNotFoundImportItems] = useState([])
	const [modalNotFoundImport, setModalNotFoundImport] = useState(false)
	const [productTypes, setProductTypes] = useState([])
	const [modalTemplateExcel, setModalTemplateExcel] = useState({ 'bool': false, 'product_type_ids': [] })
	const [data, setData] = useState({
		'pos_id': reduxAccount.pos_id,
		'wms_id': reduxAccount.wms_id,
		'elements': [],
		'created_at': formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
		'surplus': 0,
		'shortage': 0,
		'counted_amount': 0,
		'counted_quantity': 0,
		'difference_amount': 0,
		'divergence': 0,
	})
	// TEMPLATE
	const [products, setProducts] = useState([])
	const [modalTemplate, setModalTemplate] = useState({ 'bool': false, 'templates': [] })
	const [modalTemplateCRUD, setModalTemplateCRUD] = useState({
		bool: false,
		id: null,
		name: '',
		products: [],
	})
	// TEMPLATE

	// В DOM держим только видимые строки — иначе при 2000 позициях
	// каждое нажатие клавиши перерисовывает весь документ
	const virtual = useVirtualRows({
		count: data.elements.length,
		rowHeight: ROW_HEIGHT,
	})

	// В выпадающем списке рисуем ограниченную выдачу: 2000 <tr> в меню
	// подвешивают браузер на самом открытии
	const elementOptions = useMemo(() => elements.slice(0, OPTIONS_LIMIT), [elements])

	// Первая строка каждого товара: в ней показываем остаток и разницу по товару целиком
	const firstRowByElement = useMemo(() => {
		const result = {}
		data.elements.forEach((item, index) => {
			if (item.element_id && result[item.element_id] === undefined) result[item.element_id] = index
		})
		return result
	}, [data])

	// Склад один на весь документ, и при проведении все строки уходят на него.
	// Сменить склад после сканирования — значит записать пересчёт одного склада
	// фактом другого, поэтому при непустом списке склад закрыт
	const hasElements = data.elements.some(item => item.element_id)

	function mapTemplateProductsToCatalog(templateRows, catalog) {
		const items = []
		for (let i = 0; i < templateRows.length; i++) {
			const row = templateRows[i]
			const pid = row.product_id
			const found = catalog.find(
				(p) => (p.product_id ?? p.element_id ?? p.id) === pid,
			)
			if (found) {
				items.push({ ...found })
			} else {
				items.push({
					product_id: pid,
					element_id: pid,
					name: row.name ?? '',
					product_type_name: row.product_type_name ?? '',
				})
			}
		}
		return items
	}

	async function inventoryCompleted(completed = false) {
		// Разницу пересчитываем по товару целиком, а не построчно: строки из шаблона
		// и «Не инвентаризованных» приходят без расчёта. В повторных строках товара
		// она пустая — в базу уходит нулём
		var sendData = calculateTotals(JSON.parse(JSON.stringify(data)))
		sendData.elements = sendData.elements.map(item => ({
			...item,
			'divergence': Number(item.divergence) || 0,
			'difference_amount': Number(item.difference_amount) || 0,
		}))

		for (let i = 0; i < sendData.elements.length; i++) {
			if (sendData.elements[i]['actual_balance'] < 0 || sendData.elements[i]['actual_balance'] === "") {
				sendData.error = true
			}
		}

		if (sendData.error) {
			notify(t('check_filled_inputs'))
			return
		}

		var response;
		if (id) {
			if (completed) {
				response = await PUT('/inventory-completed', sendData, { loader: true })
				if (httpOk(response)) {
					flushSync(() => setIsDirty(false))
					navigate('/inventory-wms')
				}
			} else {
				response = await PUT('/inventory', sendData, { loader: true })
				if (httpOk(response)) {
					flushSync(() => setIsDirty(false))
					navigate('/inventory-wms')
				}
			}
		} else {
			if (completed) {
				response = await PUT('/inventory-completed', sendData, { loader: true })
				if (httpOk(response)) {
					flushSync(() => setIsDirty(false))
					navigate('/inventory-wms')
				}
			} else {
				response = await POST('/inventory', sendData, { loader: true })
				if (httpOk(response)) {
					flushSync(() => setIsDirty(false))
					navigate('/inventory-wms')
				}
			}
		}
	}

	function handleProductsChange(e, index) {
		var dataCopy = { ...data }
		if (Number(e.target.value) > 999999999) {
			notify(t('exceeds_maximum'))
			dataCopy.elements[index][e.target.name] = ""
			setData(dataCopy)
			return;
		}

		// Разрешаем минус (отрицательный остаток) и до 3 знаков после запятой
		// Запятую (русская раскладка, точка на цифровом блоке) приводим к точке
		let value = e.target.value.replace(/,/g, '.').replace(/[^0-9.-]/g, '')
		const parts = value.split('.')
		if (parts.length > 2) {
			value = parts[0] + '.' + parts.slice(1).join('')
		}
		if (parts[1]?.length > 3) {
			value = parts[0] + '.' + parts[1].slice(0, 3)
		}
		dataCopy.elements[index][e.target.name] = value
		calculate(dataCopy)
	}

	function deleteProduct(index) {
		var dataCopy = { ...data }
		dataCopy.elements.splice(index, 1)
		// Удалили первую строку товара — его разница переезжает в следующую
		calculate(dataCopy)
	}

	function returnEmptyElement() {
		return {
			"price": "",
			"element_id": "",
			"actual_balance": "",
			"name": "",
			"product_type_name": "",
			"balance": "",
			"unit_name": "",
			"divergence": '',
			"difference_amount": '',
		}
	}

	function addToList(length = 1) {
		var dataCopy = JSON.parse(JSON.stringify(data))
		var firstNewIndex = dataCopy.elements.length

		for (let i = 0; i < length; i++) {
			dataCopy.elements.push(returnEmptyElement())
		}

		setIsDirty(true)
		setData(dataCopy)

		// Новая строка может быть за пределами окна виртуализации —
		// сначала подводим её к экрану, потом ставим курсор
		setTimeout(() => {
			virtual.scrollToIndex(firstNewIndex, `input-select-${firstNewIndex + 1}`)
		}, 60);
	}

	async function recalculateBalance(elements) {
		var dataCopy = { ...data }

		for (let i = 0; i < dataCopy.elements.length; i++) {
			for (let j = 0; j < elements.length; j++) {
				if (dataCopy.elements[i]['element_id'] === elements[j]['element_id']) {
					dataCopy.elements[i]['balance'] = elements[j]['balance'];
					break
				}
			}
		}

		calculate(dataCopy)
	}

	function calculate(dataCopy) {
		setData(calculateTotals(dataCopy))
	}

	function calculateTotals(dataCopy) {
		dataCopy.balance = 0
		dataCopy.divergence = 0
		dataCopy.difference_amount = 0
		dataCopy.surplus = 0
		dataCopy.shortage = 0
		dataCopy.counted_amount = 0
		dataCopy.counted_quantity = 0

		// Один товар может стоять несколькими строками (каждое место отдельным сканом).
		// Учётный остаток у них общий, поэтому разницу считаем по товару целиком
		// и показываем в первой его строке — так же сводит строки бэк при проведении
		const actualByElement = {}
		for (let i = 0; i < dataCopy.elements.length; i++) {
			const elementId = dataCopy.elements[i]['element_id']
			if (!elementId) continue
			actualByElement[elementId] = (actualByElement[elementId] || 0) + (Number(dataCopy.elements[i]['actual_balance']) || 0)
		}

		const countedElements = {}
		for (let i = 0; i < dataCopy.elements.length; i++) {
			const element = dataCopy.elements[i]
			const actualBalance = Number(element['actual_balance']) || 0
			const price = Number(element['price']) || 0

			dataCopy.counted_quantity += actualBalance
			dataCopy.counted_amount += actualBalance * price

			if (element['element_id'] && countedElements[element['element_id']]) {
				element['divergence'] = ''
				element['difference_amount'] = ''
				continue
			}
			if (element['element_id']) countedElements[element['element_id']] = true

			const balance = Number(element['balance']) || 0
			const totalActual = element['element_id'] ? actualByElement[element['element_id']] : actualBalance
			const divergence = totalActual - balance
			const differenceAmount = divergence * price

			element['divergence'] = divergence
			element['difference_amount'] = differenceAmount

			dataCopy.divergence += divergence
			dataCopy.difference_amount += differenceAmount
			if (differenceAmount > 0) dataCopy.surplus += differenceAmount
			if (differenceAmount < 0) dataCopy.shortage += Math.abs(differenceAmount)
		}

		return dataCopy
	}

	async function notInventoredList() {
		// var arrayIds = []
		// for (let i = 0; i < data.elements.length; i++) {
		// 	arrayIds.push(data.elements[i]['element_id'])
		// }
		// var sendData = {
		// 	"balance_ids": [],
		// 	"pos_id": data.pos_id,
		// 	"wms_id": data.wms_id
		// }
		// sendData.balance_ids = arrayIds

		// const response = await POST('/inventory/not-inventored', sendData, { loader: true })
		// if (httpOk(response)) {
		// 	var list = [...response.data]
		// 	for (let i = 0; i < list.length; i++) {
		// 		list[i]['selected'] = false
		// 		list[i]['actual_balance'] = list[i]['balance']
		// 	}

		// 	setNotInventoredProducts(list)
		setModalNotInventored(true)
		//}
	}

	// REACTSELECT LOGIC
	function handleSelectSearch(value) {
		if (!value) {
			setElements([...elementsDuplicate])
			return
		}
		var elementsDuplicateCopy = [...elementsDuplicate]

		var filteredItems = elementsDuplicateCopy.filter(item => item.name.toLowerCase().includes(value.toLowerCase()))
		if (filteredItems[0]) {
			filteredItems[0]['selected'] = true
		}
		setElements(filteredItems)
	}

	function handleSelectElement(option, index) {
		var dataCopy = { ...data }

		dataCopy.elements[index]['balance_id'] = option.balance_id
		dataCopy.elements[index]['element_id'] = option.element_id
		dataCopy.elements[index]['name'] = option.name
		dataCopy.elements[index]['product_type_name'] = option.product_type_name
		dataCopy.elements[index]['unit_name'] = option.unit_name
		dataCopy.elements[index]['price'] = option.price
		dataCopy.elements[index]['balance'] = option.balance
		dataCopy.elements[index]['actual_balance'] = ''
		dataCopy.elements[index]['divergence'] = ''
		dataCopy.elements[index]['difference_amount'] = ''

		setData(dataCopy)
	}

	// Сканер: штрих-код товара — +1 шт, весовой штрих-код (префикс 20/21) — PLU и вес из кода.
	// Разбор весового кода тот же, что в приготовлении (documentsPreparation/CreateUpdate)
	function handleBarcodeScan(value, index) {
		value = value.trim()
		setElements([...elementsDuplicate])

		var quantity = 1
		var found = elementsDuplicate.find(item => item.barcode && String(item.barcode) === value)

		if (!found && (value.startsWith('20') || value.startsWith('21'))) {
			var plu = parseInt(value.substring(4, 7), 10)
			found = elementsDuplicate.find(item => item.plu !== null && item.plu !== '' && parseInt(item.plu, 10) === plu)
			if (found) {
				quantity = value.startsWith('20')
					? Number(value.substring(7, 12)) / 1000
					: Number(value.substring(10, 12))
			}
		}

		if (!found) {
			notify(`${t('product_by_barcode_not_found')}: ${value}`)
			return
		}

		var dataCopy = JSON.parse(JSON.stringify(data))

		// Каждый скан — своей строкой, даже если товар уже в документе: клиент видит,
		// сколько мест пересчитал и сколько весило каждое. При проведении бэк
		// складывает строки одного товара в одну
		var targetIndex = index
		if (dataCopy.elements[index]?.element_id) {
			dataCopy.elements.push(returnEmptyElement())
			targetIndex = dataCopy.elements.length - 1
		}

		dataCopy.elements[targetIndex] = {
			...dataCopy.elements[targetIndex],
			'balance_id': found.balance_id,
			'element_id': found.element_id,
			'name': found.name,
			'product_type_name': found.product_type_name,
			'unit_name': found.unit_name,
			'price': found.price,
			'balance': found.balance,
			'actual_balance': formatInventoryQty(quantity),
		}

		// Внизу всегда держим пустую строку под следующий скан
		var lastElement = dataCopy.elements[dataCopy.elements.length - 1]
		var nextIndex = dataCopy.elements.length - 1
		if (lastElement?.element_id) {
			dataCopy.elements.push(returnEmptyElement())
			nextIndex = dataCopy.elements.length - 1
		}

		setIsDirty(true)
		calculate(dataCopy)

		setTimeout(() => {
			virtual.scrollToIndex(nextIndex, `input-select-${nextIndex + 1}`)
		}, 60);
	}

	function closeReactSelect() {
		// Через функциональный апдейт: скан успевает вернуть полный список,
		// и снимок elements из замыкания затёр бы его отфильтрованным
		setTimeout(() => {
			setElements(prevElements => {
				var elementsCopy = [...prevElements]
				for (let i = 0; i < elementsCopy.length; i++) {
					elementsCopy[i]['selected'] = false
				}
				return elementsCopy
			})
		}, 100);
	}

	function handleTableKeyDown(event) {
		event.stopPropagation();
		// Стрелками ходим по тому, что реально показано в меню
		var currentIndex = elementOptions.findIndex(el => el.selected);
		if (currentIndex >= 0) {
			elementOptions[currentIndex].selected = false
			switch (event.key) {
				case "ArrowUp":
					currentIndex = currentIndex === 0 ? elementOptions.length - 1 : currentIndex - 1
					break;
				case "ArrowDown":
					currentIndex = currentIndex === elementOptions.length - 1 ? 0 : currentIndex + 1
					break;
				default: return;
			}

			elementOptions[currentIndex].selected = true
			if (scrollRef.current) {
				scrollRef.current.scrollTop = (currentIndex * 29.6) - 29.6
			}

			setElements([...elements])
		}
	}
	// REACTSELECT LOGIC

	// TEMPLATE LOGIC
	async function toggleModalTemplate(bool) {
		if (bool) {
			const response = await GET(`/documents-templates`, {}, { loader: true })
			if (httpOk(response)) {
				setModalTemplate({ ...modalTemplate, 'bool': bool, 'templates': response.data })
			}
		} else {
			setModalTemplate({ ...modalTemplate, 'bool': bool, 'templates': [] })
		}
	}

	async function refreshTemplatesList() {
		const response = await GET('/documents-templates', {}, { loader: false })
		if (httpOk(response)) {
			setModalTemplate((prev) => ({ ...prev, templates: response.data }))
		}
	}

	async function openTemplateEditor(template = null) {
		const response = await GET('/documents-templates-all-products', {}, { loader: true })
		if (!httpOk(response)) return
		const catalog = response.data || []

		if (template) {
			const tplRes = await GET(`/documents-templates-products/${template.id}`, {}, { loader: true })
			if (!httpOk(tplRes)) return
			const selected = mapTemplateProductsToCatalog(tplRes.data || [], catalog)
			setProducts(catalog)
			setModalTemplateCRUD({
				bool: true,
				id: template.id,
				name: template.name ?? '',
				products: selected,
			})
		} else {
			setProducts(catalog)
			setModalTemplateCRUD({
				bool: true,
				id: null,
				name: '',
				products: [],
			})
		}
	}

	async function toggleModalTemplateCRUD(bool) {
		if (bool) {
			await openTemplateEditor(null)
		} else {
			setModalTemplateCRUD({ bool: false, id: null, name: '', products: [] })
		}
	}

	async function editTemplate(item, e) {
		e.stopPropagation()
		await openTemplateEditor(item)
	}

	async function handleSelectTemplate(item) {
		var response = await GET(`/documents-templates-products/${item.id}`, {}, { loader: true })
		if (httpOk(response)) {
			response = response.data
			
			var items = []
			for (let i = 0; i < response.length; i++) {
				for (let j = 0; j < elementsDuplicate.length; j++) {
					if (response[i]['product_id'] === elementsDuplicate[j]['element_id']) {
						elementsDuplicate[j]['element_id'] = response[i]['product_id'] // изменить потом на product_id
						items.push(elementsDuplicate[j])
					}
				}
			}

			setData({ ...data, 'elements': items })
			toggleModalTemplate(false)
		}
	}

	async function deleteTemplate(item, e) {
		e.stopPropagation()
		const response = await DELETE(`/documents-templates/${item.id}`, {}, { loader: true })
		if (httpOk(response)) refreshTemplatesList()
	}
	// TEMPLATE LOGIC

	// EXCEL LOGIC
	// Шаблон выгружаем справочником с пустой колонкой «Посчитано»,
	// клиент считает офлайн и заливает тот же файл обратно.
	// Типы выбираются галочками: инвентаризируют обычно не всё сразу, а отдельно элементы,
	// отдельно заготовки — иначе в файл падает вся номенклатура
	function toggleModalTemplateExcel(bool) {
		if (bool) {
			setModalTemplateExcel({
				'bool': true,
				'product_type_ids': productTypes
					.filter((item) => item.id === PRODUCT_TYPE_ELEMENT)
					.map((item) => item.id),
			})
			return
		}

		setModalTemplateExcel({ ...modalTemplateExcel, 'bool': false })
	}

	function toggleTemplateProductType(id, checked) {
		const productTypeIds = checked
			? [...modalTemplateExcel.product_type_ids, id]
			: modalTemplateExcel.product_type_ids.filter((item) => item !== id)

		setModalTemplateExcel({ ...modalTemplateExcel, 'product_type_ids': productTypeIds })
	}

	function toggleAllTemplateProductTypes() {
		const allIds = productTypes.map((item) => item.id)
		const productTypeIds = modalTemplateExcel.product_type_ids.length === allIds.length ? [] : allIds

		setModalTemplateExcel({ ...modalTemplateExcel, 'product_type_ids': productTypeIds })
	}

	// «Выбрать все» — чекбокс: отмечены все типы → галочка, часть → «минус»
	const allProductTypesChecked = productTypes.length > 0
		&& modalTemplateExcel.product_type_ids.length === productTypes.length
	const someProductTypesChecked = modalTemplateExcel.product_type_ids.length > 0
		&& !allProductTypesChecked

	function saveExcelFile(response, fileName) {
		const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

		const fileUrl = URL.createObjectURL(file);

		const link = document.createElement('a');
		link.href = fileUrl;
		link.setAttribute('download', fileName);
		document.body.appendChild(link);
		link.click();
		link.remove();

		URL.revokeObjectURL(fileUrl);
	}

	async function downloadTemplate() {
		// Склад документа обязателен: без него бэк отдаёт номенклатуру всех складов точки,
		// и в файл падают позиции, которые на этом складе не считают
		const urlParams = `?product_type_ids=${modalTemplateExcel.product_type_ids.join(',')}`
			+ `&pos_id=${data.pos_id}&wms_id=${data.wms_id}`
		const response = await FILE_D(`/inventory-excel-template${urlParams}`, { loader: true })
		if (httpOk(response)) {
			saveExcelFile(response, `${t('inventory')}.xlsx`)
			toggleModalTemplateExcel(false)
		}
	}

	// Сохранённый шаблон клиента (его же набор позиций) выгружаем тем же файлом:
	// посчитали офлайн — залили обратно через импорт
	async function downloadTemplateExcel(item, e) {
		e.stopPropagation()

		const response = await FILE_D(`/inventory-excel-template?document_template_id=${item.id}`, { loader: true })
		if (httpOk(response)) {
			saveExcelFile(response, `${item.name || t('templates')}.xlsx`)
		}
	}

	// Загруженный файл заменяет позиции документа: остаток и цену берём из справочника
	// на выбранную дату, из файла — только «Посчитано»
	async function importFromExcel(e) {
		if (!e.target.files?.[0]) return

		let formData = new FormData();
		formData.append("file", e.target.files[0]);

		const response = await FILE('/inventory-excel-import', formData, { loader: true });
		if (httpOk(response)) {
			const importedRows = response?.data?.data || []
			const elementsMap = {}
			for (let i = 0; i < elementsDuplicate.length; i++) {
				elementsMap[elementsDuplicate[i]['element_id']] = elementsDuplicate[i]
			}

			const items = []
			const notFound = []
			for (let i = 0; i < importedRows.length; i++) {
				const row = importedRows[i]
				const element = elementsMap[row.element_id]
				if (!element) {
					notFound.push(row)
					continue
				}

				items.push({
					'balance_id': element.balance_id,
					'element_id': element.element_id,
					'name': element.name,
					'product_type_name': element.product_type_name,
					'unit_name': element.unit_name,
					'price': element.price,
					'balance': element.balance,
					'actual_balance': String(row.actual_balance ?? ''),
					'divergence': '',
					'difference_amount': '',
				})
			}

			// Если не совпало вообще ничего — документ не трогаем, только показываем список
			if (items.length) {
				setIsDirty(true)
				calculate({ ...data, 'elements': items })
			}

			setNotFoundImportItems(notFound)
			if (notFound.length) setModalNotFoundImport(true)
		}

		document.getElementById('actual-btn-inventory-import').value = '';
	}
	// EXCEL LOGIC

	async function getData() {
		if (!id) getElements(reduxAccount.wms_id)

		if (id) {
			const response = await GET(`/inventory/${id}`)
			if (httpOk(response)) setData(response.data)
		}
	}

	async function getWms() {
		const response = await GET(`/helper/wms?pos_id=${reduxAccount.pos_id}`)
		if (httpOk(response)) setWms(response.data)
	}

	async function getProductTypes() {
		const response = await GET('/helper/product-types')
		if (httpOk(response)) setProductTypes(response.data)
	}

	async function getElements() {
		// Склад не выбран (у аккаунта нет склада по умолчанию) — запрашивать нечего, иначе в запрос уйдёт wms_id=undefined
		if (!data.wms_id) {
			setElements([])
			setElementsDuplicate([])
			return
		}

		var sendData = {
			"pos_id": reduxAccount.pos_id,
			"wms_id": data.wms_id,
			"created_at": formatDate(data.created_at, 'yyyy-MM-dd'),
		}
		const response = await GET(`/inventory-elements`, sendData, { loader: true })
		if (httpOk(response)) {
			setElements(response.data)
			setElementsDuplicate(response.data)

			recalculateBalance(response.data)
		}
	}

	useEffect(() => {
		getData()
		getWms()
		getProductTypes()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useDidMountEffect(() => {
		getElements()
	}, [formatDate(data.created_at, 'yyyy-MM-dd'), data.wms_id]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between flex-wrap mb-2">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('inventory')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{t('create')}</h6>
					</div>
				</div>
				<div className="d-flex gap-2">
					<button className="btn btn-primary"
						onClick={() => notInventoredList()}>
						{t('not_inventored')}
					</button>
					<DropdownButton title={t('action')}>
						<Dropdown.Item onClick={() => toggleModalTemplateExcel(true)}>
							{t('download_template')}
						</Dropdown.Item>
						<Dropdown.Item as="li">
							<input
								id="actual-btn-inventory-import"
								hidden
								type="file"
								onChange={importFromExcel}
								accept=".xlsx, application/vnd.ms-excel"
							/>
							<label htmlFor="actual-btn-inventory-import" className="upload-from-excel">
								{t('import')}
							</label>
						</Dropdown.Item>
						<hr className="m-0" />
						<Dropdown.Item onClick={() => toggleModalTemplate(true)}>
							{t('templates')}
						</Dropdown.Item>
						<Dropdown.Item onClick={() => toggleModalTemplateCRUD(true)}>
							{t('create_template')}
						</Dropdown.Item>
					</DropdownButton>
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<div className="row">
						<div className="col-md-3">
							<div className="form-group">
								<label>{t('date')}<span className="required-mark">*</span></label>
								<div className="calendar-input">
									<DatePicker
										showMonthDropdown
										showYearDropdown
										dropdownMode="select"
										yearDropdownItemNumber={2}
										className="form-control"
										showTimeSelect
										timeFormat="HH:mm"
										timeIntervals={30}
										timeCaption={t('time')}
										dateFormat="dd.MM.yyyy HH:mm"
										selected={data.created_at ? new Date(data.created_at) : ''}
										onChange={(date) => setData({ ...data, 'created_at': formatDate(date, "yyyy-MM-dd'T'HH:mm:ss") })}
										minDate={returnMinDate(new Date(), 20)}
										maxDate={new Date()}
										minTime={new Date(new Date().setHours(0, 0, 0, 0))}
										maxTime={formatDate(data.created_at, 'yyyy-MM-dd') === formatDate(new Date(), 'yyyy-MM-dd') ? new Date() : new Date(new Date().setHours(23, 59, 0, 0))} />
									<i className="uil uil-calendar-alt"></i>
								</div>
							</div>
						</div>
						<div className="col-md-2">
							<div className="form-groups">
								<label>{t('wms')}<span className="required-mark">*</span></label>
								<Select
									isDisabled={data.id || hasElements}
									value={wms.find(option => option.id === data.wms_id)}
									options={wms}
									onChange={(option) => {
										if (hasElements) return
										setData({ ...data, 'wms_id': option.id, 'wms_name': option.name })
									}}
									menuPosition="fixed"
									menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
									menuPlacement="auto"
									styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
								{!data.id && hasElements &&
									<small className="text-muted">{t('inventory_wms_locked')}</small>
								}
							</div>
						</div>
						<div className="col-md-2">
							<div className="form-group">
								<label>{t('note')}</label>
								<input name="note" className="form-control"
									autoComplete="off"
									value={data.note ?? ''}
									onChange={(e) => setData({ ...data, 'note': e.target.value })} />
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="doc-table-wrap"
						ref={virtual.containerRef}
						style={{ 'maxHeight': 'calc(100vh - 430px)' }}>
						<table className="table table-bordered doc-table" style={{ 'minWidth': '1080px' }}>
							<colgroup>
								<col style={{ 'width': '33px' }} />
								<col />
								<col style={{ 'width': '130px' }} />
								<col style={{ 'width': '110px' }} />
								<col style={{ 'width': '100px' }} />
								<col style={{ 'width': '70px' }} />
								<col style={{ 'width': '120px' }} />
								<col style={{ 'width': '110px' }} />
								<col style={{ 'width': '130px' }} />
								<col style={{ 'width': '44px' }} />
							</colgroup>
							<thead>
								<tr>
									<th className="text-center">#</th>
									<th>
										<div className="position-relative">
											{t('name2')}
											<div className={`position-absolute cursor ${isBarcodeSearchEnabled ? 'text-primary' : ''}`}
												style={{
													'top': 0,
													'right': '5px'
												}}
												title={t('barcode')}
												onClick={() => setIsBarcodeSearchEnabled(!isBarcodeSearchEnabled)}>
												<i className="uil-qrcode-scan"></i>
											</div>
										</div>
									</th>
									<th>{t('product_type')}</th>
									<th className="text-end">{t('price')}</th>
									<th className="text-end">{t('balance2')}</th>
									<th className="text-start">{t('unit')}</th>
									<th className="text-end">{t('counted')}</th>
									<th className="text-end">{t('difference')}</th>
									<th className="text-end">{t('amount')}</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{virtual.topSpacer > 0 &&
									<tr className="doc-row-spacer" aria-hidden="true">
										<td colSpan={10} style={{ 'height': `${virtual.topSpacer}px` }}></td>
									</tr>
								}
								{data.elements.length === 0 &&
									<tr>
										<td colSpan={10} className="text-center text-muted">{t('list_empty')}</td>
									</tr>
								}
								{data.elements.slice(virtual.start, virtual.end).map((item, sliceIndex) => {
									const index = virtual.start + sliceIndex
									// Повторная строка того же товара: остаток и разница — в первой его строке
									const isRepeatRow = !!item.element_id && firstRowByElement[item.element_id] !== index
									return (
								<tr key={index}>
									<td className="text-center">{index + 1}</td>
									<td>
										<ReactSelect
											search={handleSelectSearch}
											handleSelectSearch={(value) => handleBarcodeScan(value, index)}
											isBarcodeSearchEnabled={isBarcodeSearchEnabled}
											value={item.name}
											index={index}
											scrollRef={scrollRef}
											options={elements}
											onClose={() => closeReactSelect()}
											selectItem={(option) => handleSelectElement(option, index)}
											onKeyUp={(e) => {
												if (e.keyCode === 40 || e.keyCode === 38) {
													handleTableKeyDown(e)
												}
											}}
											table={() => (
												<>
													<table className="table mb-0">
														<tbody ref={tbodyRef}>
															{elementOptions.map((selectItem, selectIndex) => (
																<tr
																	id={selectItem.id}
																	className={`cursor ${((selectItem.selected || (selectItem.id === item.element_id)) ? 'selected' : '')}`}
																	tabIndex={-1}
																	key={selectIndex}
																	onClick={() => handleSelectElement(selectItem, index)}
																	onKeyDown={(e) => handleTableKeyDown(e, selectItem, index)}>
																	<td>{selectItem.name}</td>
																	<td className="text-end">{selectItem.product_type_name}</td>
																</tr>
															))}
														</tbody>
													</table>
													{elements.length > elementOptions.length &&
														<div className="doc-options-hint">
															{t('inventory_options_hint', {
																shown: elementOptions.length,
																total: elements.length,
															})}
														</div>
													}
												</>
											)}
										/>
									</td>
									<td className="vertical-middle text-nowrap">{item.product_type_name || '—'}</td>
									<td className="vertical-middle text-end text-nowrap">{formatMoney(item.price)}</td>
									<td className="vertical-middle text-end text-nowrap">{isRepeatRow ? '' : formatInventoryQty(item.balance)}</td>
									<td className="vertical-middle">{item.unit_name}</td>
									<td className={`td-input ${!item.actual_balance && item.actual_balance !== 0 ? 'is-empty' : ''}`}>
										<input
											type="text"
											name="actual_balance"
											autoComplete="off"
											className={`form-control text-end ${item.actual_balance < 0 ? 'required-border' : ''}`}
											placeholder="0"
											value={item.actual_balance === '' || item.actual_balance === null || item.actual_balance === undefined
												? ''
												: item.actual_balance}
											onChange={(e) => handleProductsChange(e, index)}
											onKeyDown={(e) => handleDecimalKeyDown(e, (event) => handleProductsChange(event, index))}
											onBlur={(e) => {
												if (e.target.value === '' || e.target.value === '-' || e.target.value === '.') return
												var dataCopy = { ...data }
												dataCopy.elements[index]['actual_balance'] = formatInventoryQty(e.target.value)
												calculate(dataCopy)
											}}
											onKeyUp={(event) => {
												if (event.key === 'Enter') {
													addToList()
												}
											}} />
									</td>
									{isRepeatRow
										? <td colSpan={2} className="text-end text-nowrap text-muted"
											title={t('inventory_repeat_row_hint')}>
											{t('inventory_repeat_row', { row: firstRowByElement[item.element_id] + 1 })}
										</td>
										: <>
											<td className="text-end text-nowrap">
												<span className={`${Number(item.divergence) > 0 ? 'text-success' : Number(item.divergence) < 0 ? 'text-danger' : ''}`}>
													{formatInventoryQty(item.divergence)}
												</span>
											</td>
											<td className="text-end text-nowrap">
												<span className={`${Number(item.difference_amount) > 0 ? 'text-success' : Number(item.difference_amount) < 0 ? 'text-danger' : ''}`}>
													{formatMoney(item.difference_amount, 2)}
												</span>
											</td>
										</>
									}
									<td className="vertical-middle">
										<div className="d-flex justify-content-center"
											onClick={() => deleteProduct(index)}>
											<div className="table-action-button table-action-danger-button">
												<i className="uil uil-times"></i>
											</div>
										</div>
									</td>
								</tr>
									)
								})}
								{virtual.bottomSpacer > 0 &&
									<tr className="doc-row-spacer" aria-hidden="true">
										<td colSpan={10} style={{ 'height': `${virtual.bottomSpacer}px` }}></td>
									</tr>
								}
							</tbody>
							<tfoot>
								<tr>
									<td></td>
									<td>{t('total')}</td>
									<td></td>
									<td></td>
									<td></td>
									<td></td>
									<td className="text-end">{formatInventoryQty(data.counted_quantity)}</td>
									<td className="text-end">{formatInventoryQty(data.divergence)}</td>
									<td className="text-end">{formatMoney(data.difference_amount)}</td>
									<td></td>
								</tr>
							</tfoot>
						</table>
					</div>

					<div className="doc-footbar">
						<div className="doc-toolbar-actions">
							<input
								type="text"
								className="form-control form-control-sm doc-add-quantity"
								title={t('quantity')}
								tabIndex={-1}
								value={addQuantity}
								onChange={(e) => setAddQuantity(e.target.value.replace(/[^\d]/g, ''))}
							/>
							<button type="button" className="btn btn-sm btn-primary"
								disabled={!data.pos_id}
								onClick={() => {
									setAddQuantity(1)
									addToList(addQuantity)
								}}>
								<i className="uil uil-plus"></i> {t('add')}
							</button>
							<button type="button" className="btn btn-sm btn-outline-primary"
								title={t('refresh')}
								onClick={() => getElements()}>
								<i className="uil uil-sync"></i>
							</button>
						</div>

						<div className="doc-summary">
							<div className="doc-summary-item is-surplus">
								<span className="doc-summary-label">{t('surplus')}</span>
								<span className="doc-summary-value">{formatMoney(data.surplus)}</span>
							</div>
							<div className="doc-summary-item is-shortage">
								<span className="doc-summary-label">{t('shortage')}</span>
								<span className="doc-summary-value">{formatMoney(data.shortage)}</span>
							</div>
							<div className="doc-summary-item">
								<span className="doc-summary-label">{t('counted')} ({t('amount')})</span>
								<span className="doc-summary-value">{formatMoney(data.counted_amount)}</span>
							</div>
						</div>
					</div>

					<div className="btn-wrapper gap-2 mt-5">
						<Link to="/inventory-wms" className="btn btn-warning btn-rounded btn-wide">
							{t('cancel')}
						</Link>
						<button type="button" className="btn btn-outline-primary btn-rounded btn-wide"
							disabled={!data.elements.length > 0}
							onClick={() => inventoryCompleted()}>
							{t('save_draft')}
						</button>
						<button type="button" className="btn btn-primary btn-rounded btn-wide"
							disabled={!data.elements.length > 0}
							onClick={() => setModalConfirm(true)}>
							{t('confirm')}
						</button>
					</div>
				</div>
			</div>

			<NotInventored
				data={data}
				setData={setData}
				modalNotInventored={modalNotInventored}
				setModalNotInventored={setModalNotInventored}>
			</NotInventored>

			{/* MODAL CONFIRM */}
			<Modal show={modalConfirm} animation={false} centered dialogClassName="update-modal-width"
 onHide={() => setModalConfirm(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('confirm')}</Modal.Title>
				</Modal.Header>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => setModalConfirm(false)}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => inventoryCompleted(true)}>
							{t('confirm')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>
			{/* MODAL CONFIRM */}

			{/* MODAL TEMPLATE */}
			<Modal show={modalTemplate.bool} animation={false} centered
				dialogClassName="update-modal-width" onHide={() => toggleModalTemplate(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('templates')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive">
						<table className="table table-bordered table-hover cursor mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '100%' }}>{t('name2')}</th>
									<th style={{ 'minWidth': '104px' }}></th>
								</tr>
							</thead>
							<tbody>
								{modalTemplate?.templates?.map((item, index) => (
									<tr key={item.id ?? index}>
										<td className="text-center" style={{ 'minWidth': '33px' }}>{index + 1}</td>
										<td className="w-100"
											onClick={() => handleSelectTemplate(item)}>
											{item.name}
										</td>
										<td className="text-center" style={{ 'minWidth': '104px' }}>
											<div className="d-flex justify-content-end gap-1">
												<div className="table-action-button table-action-success-button"
													title={t('download')}
													onClick={(e) => downloadTemplateExcel(item, e)}>
													<i className="uil uil-file-download-alt"></i>
												</div>
												<div className="table-action-button table-action-primary-button"
													title={t('edit')}
													onClick={(e) => editTemplate(item, e)}>
													<i className="uil uil-edit-alt"></i>
												</div>
												<div className="table-action-button table-action-danger-button"
													title={t('delete')}
													onClick={(e) => deleteTemplate(item, e)}>
													<i className="uil uil-times"></i>
												</div>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL TEMPLATE */}

			{/* MODAL TEMPLATE */}
			{modalTemplateCRUD.bool &&
				<DocumentTemplate
					products={products}
					modalTemplateCRUD={modalTemplateCRUD}
					setModalTemplateCRUD={setModalTemplateCRUD}
					toggleModalTemplateCRUD={toggleModalTemplateCRUD}
					onSaved={refreshTemplatesList}>
				</DocumentTemplate>
			}
			{/* MODAL TEMPLATE */}

			{/* MODAL NOT FOUND IMPORT ITEMS */}
			<Modal show={modalNotFoundImport} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => setModalNotFoundImport(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('not_found')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'minWidth': '80px' }} className="text-center">{t('row')}</th>
									<th style={{ 'width': '100%' }}>{t('name2')}</th>
									<th style={{ 'minWidth': '120px' }} className="text-end">{t('counted')}</th>
								</tr>
							</thead>
							<tbody>
								{notFoundImportItems.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{index + 1}</td>
										<td className="text-center">{item.row}</td>
										<td className="td-ellipsis">{item.name}</td>
										<td className="text-end">{item.actual_balance ?? ''}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL NOT FOUND IMPORT ITEMS */}

			{/* MODAL EXCEL TEMPLATE */}
			<Modal show={modalTemplateExcel.bool} animation={false} centered
				onHide={() => toggleModalTemplateExcel(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('download_template')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{/* Шаблон выгружается по складу документа — показываем, по какому именно */}
					<div className="mb-2 pb-2 border-bottom">
						<span className="text-muted">{t('wms')}: </span>
						<span className="fw-bold">
							{wms.find(option => option.id === data.wms_id)?.name ?? data.wms_name}
						</span>
					</div>
					<label className="mb-2">{t('product_type')}</label>
					<div className="form-check mb-2 pb-2 border-bottom">
						<input type="checkbox" className="form-check-input"
							id="template-product-type-all"
							ref={(el) => {
								// Отмечена часть типов — рисуем «минус» вместо галочки
								if (el) el.indeterminate = someProductTypesChecked
							}}
							checked={allProductTypesChecked}
							onChange={() => toggleAllTemplateProductTypes()} />
						<label className="form-check-label" htmlFor="template-product-type-all">
							{t('select_all')}
						</label>
					</div>
					{productTypes.map((item) => (
						<div className="form-check mb-1" key={item.id}>
							<input type="checkbox" className="form-check-input"
								id={`template-product-type-${item.id}`}
								checked={modalTemplateExcel.product_type_ids.includes(item.id)}
								onChange={(e) => toggleTemplateProductType(item.id, e.target.checked)} />
							<label className="form-check-label" htmlFor={`template-product-type-${item.id}`}>
								{item.name}
							</label>
						</div>
					))}
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalTemplateExcel(false)}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							disabled={modalTemplateExcel.product_type_ids.length === 0}
							onClick={() => downloadTemplate()}>
							{t('download')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>
			{/* MODAL EXCEL TEMPLATE */}
		</>
	)
}

export default CreateUpdate

